import React, { useEffect, useMemo, useState } from "react";

import { ChatState } from "../reducer/types";

import { ChatAnalytics, ChatAnalyticsContext } from "./analyticsContext";

interface ChatAnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that makes analytics data available throughout the component tree
 * without relying on global window objects
 */
export const ChatAnalyticsProvider: React.FC<ChatAnalyticsProviderProps> = ({
  children,
}) => {
  const [analyticsData, setLocalAnalyticsData] = useState<Partial<ChatState>>(
    ChatAnalytics.getAnalyticsData()
  );

  // Update local state and module state when data changes
  const setAnalyticsData = (data: Partial<ChatState>) => {
    setLocalAnalyticsData((prevData) => {
      const newData = { ...prevData, ...data };
      // Also update the module-level data
      ChatAnalytics.setAnalyticsData(newData);
      return newData;
    });
  };

  // Create memoized context value
  const contextValue = useMemo(
    () => ({
      sessionId: analyticsData.sessionId || null,
      messageCount: 0,
      getSessionId: ChatAnalytics.getSessionId,
      getAnalyticsData: ChatAnalytics.getAnalyticsData,
      setAnalyticsData,
    }),
    [analyticsData]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Optional: clear analytics data on provider unmount
      // ChatAnalytics.clear();
    };
  }, []);

  return (
    <ChatAnalyticsContext.Provider value={contextValue}>
      {children}
    </ChatAnalyticsContext.Provider>
  );
};
